/**
 * src/components/admin/AdminStats.js
 *
 * Pestaña de estadísticas: clicks por negocio y por promoción.
 * Lee los contadores desde /api/stats.php (PHP en el hosting).
 *
 * Nota: en desarrollo local no hay PHP, así que muestra un aviso.
 */

import { define } from '@components/Component.js'
import { h } from '@core/dom.js'
import { showToast } from './adminHelpers.js'

const RESET_TOKEN = 'ubiyaaa-reset-2026'  // ← debe coincidir con reset.php

define('AdminStats', ({ businesses = [], promos = [] }) => {
    const root = h('div', {})

    /** Reinicia el contador de UN solo anuncio */
    async function resetOne(type, id, name, btn) {
        if (!confirm(`¿Poner en cero los clicks de "${name}"?\n\nEsta acción no se puede deshacer.`)) return
        btn.disabled = true
        try {
            const res  = await fetch(`/api/reset.php?token=${RESET_TOKEN}&type=${type}&id=${id}`)
            const json = await res.json()
            if (!json.ok) throw new Error(json.error || 'error')
            showToast(`🗑 Contador de "${name}" en cero`)
            load()
        } catch (err) {
            showToast(`❌ No se pudo reiniciar: ${err.message}`, 4000)
            btn.disabled = false
        }
    }

    function table(title, icon, rows, type) {
        const tbody = h('tbody', {})
        if (!rows.length) {
            tbody.appendChild(h('tr', {},
                h('td', { colSpan: 4 },
                    h('div', { className: 'adm-empty' },
                        h('div', { className: 'adm-empty__icon' }, '📭'),
                        h('div', { className: 'adm-empty__text' }, 'Aún no hay clicks registrados')
                    )
                )
            ))
        } else {
            const max = rows[0].clicks || 1
            rows.forEach((r, i) => {
                const bar = h('div', {
                    style: `background:#eef2ff;border-radius:6px;height:18px;width:${Math.max(4, Math.round(r.clicks / max * 100))}%;` +
                           'display:flex;align-items:center;padding:0 .5rem;font-size:.75rem;font-weight:700;color:#4f46e5'
                }, String(r.clicks))
                const nameCell = h('td', {}, h('strong', {}, r.name))
                if (r.plan) {
                    nameCell.appendChild(h('span', {
                        className: `adm-badge adm-badge--${r.plan}`,
                        style: 'margin-left:.5rem'
                    }, r.plan === 'oro' ? '⭐ Oro' : '🥈 Plata'))
                }

                const resetBtn = h('button', {
                    className: 'adm-btn adm-btn--danger adm-btn--sm',
                    type: 'button',
                    title: `Reiniciar clicks de ${r.name}`
                }, '🗑')
                resetBtn.addEventListener('click', () => resetOne(type, r.id, r.name, resetBtn))

                tbody.appendChild(h('tr', {},
                    h('td', { style: 'width:30px;color:#94a3b8;font-weight:600' }, `${i + 1}`),
                    nameCell,
                    h('td', { style: 'width:45%' }, bar),
                    h('td', { style: 'width:60px' }, r.clicks > 0 ? resetBtn : '')
                ))
            })
        }

        return h('div', { className: 'admin-panel', style: 'margin-bottom:1.25rem' },
            h('div', { className: 'admin-panel__head' },
                h('span', { className: 'admin-panel__title' }, `${icon} ${title}`)
            ),
            h('div', { style: 'overflow-x:auto' },
                h('table', { className: 'admin-table' },
                    h('thead', {},
                        h('tr', {},
                            h('th', {}, '#'),
                            h('th', {}, 'Nombre'),
                            h('th', {}, 'Clicks'),
                            h('th', {}, 'Reiniciar'),
                        )
                    ),
                    tbody
                )
            )
        )
    }

    async function load() {
        root.innerHTML = '<p style="padding:1.5rem;color:#64748b">⏳ Cargando estadísticas…</p>'
        try {
            const res = await fetch('/api/stats.php')
            const text = await res.text()
            let stats
            try { stats = JSON.parse(text) } catch {
                throw new Error('local')
            }

            const bizRows = businesses
                .map(b => ({ id: b.id, name: b.name, plan: b.plan, clicks: stats[`business:${b.id}`] ?? 0 }))
                .sort((a, b) => b.clicks - a.clicks)

            const promoRows = promos
                .map(p => ({ id: p.id, name: p.title, clicks: stats[`promo:${p.id}`] ?? 0 }))
                .sort((a, b) => b.clicks - a.clicks)

            const totalBiz   = bizRows.reduce((s, r) => s + r.clicks, 0)
            const totalPromo = promoRows.reduce((s, r) => s + r.clicks, 0)

            root.innerHTML = ''
            root.appendChild(
                h('div', { className: 'adm-export-bar', style: 'background:#eef2ff;border-color:#c7d2fe' },
                    h('div', { className: 'adm-export-bar__text' },
                        h('span', {}, `👆 Total de clicks — Negocios: `, h('strong', {}, String(totalBiz)),
                            ` · Promociones: `, h('strong', {}, String(totalPromo)))
                    ),
                    h('div', { className: 'adm-export-btns' },
                        (() => {
                            const b = h('button', { className: 'adm-btn adm-btn--ghost', type: 'button' }, '🔄 Actualizar')
                            b.addEventListener('click', load)
                            return b
                        })(),
                        (() => {
                            const b = h('button', { className: 'adm-btn adm-btn--danger', type: 'button' }, '🗑 Reiniciar contadores')
                            b.addEventListener('click', async () => {
                                if (!confirm('¿Poner TODOS los contadores en cero?\n\nEsta acción no se puede deshacer. Útil al iniciar un nuevo mes o periodo.')) return
                                b.disabled = true
                                try {
                                    const res = await fetch(`/api/reset.php?token=${RESET_TOKEN}`)
                                    const json = await res.json()
                                    if (!json.ok) throw new Error(json.error || 'error')
                                    showToast('🗑 Contadores reiniciados a cero')
                                    load()
                                } catch (err) {
                                    showToast(`❌ No se pudo reiniciar: ${err.message}`, 4000)
                                    b.disabled = false
                                }
                            })
                            return b
                        })()
                    )
                )
            )
            root.appendChild(table('Clicks por negocio', '🏪', bizRows, 'business'))
            root.appendChild(table('Clicks por promoción', '🎁', promoRows, 'promo'))
        } catch {
            root.innerHTML = ''
            root.appendChild(
                h('div', { className: 'adm-empty' },
                    h('div', { className: 'adm-empty__icon' }, '📊'),
                    h('div', { className: 'adm-empty__text' },
                        'Las estadísticas solo están disponibles en el sitio publicado (ubiyaaacomercio.com/admin), no en desarrollo local.')
                )
            )
        }
    }

    load()
    return root
})
